import React, { useState, useMemo, useCallback } from 'react';
import { AlertCircle, RefreshCcw, X, ArrowRight } from 'lucide-react';
import { SmartDiffInput, SmartDiffList } from './SmartDiffFields';
import TensionEditor from './TensionEditor';
import MeetingPanelEmptyState from './MeetingPanelEmptyState';
import MeetingPanelTactical from './MeetingPanelTactical';
import MeetingPanelPilotage from './MeetingPanelPilotage';

// --- Main Component ---

const MeetingPanel = ({
  selectedCircle,
  activeMeeting,
  meetingError,
  meetingParticipants,
  meetingDraft,
  meetingTensions,
  selectedMeetingTensionId,
  meetingNotes,
  presenceCount,
  connectedUsers = [],
  realtimeConnected,
  meetingFormVisible = false,
  meetingPoints = [],
  meetingPreparedPoints = [],
  meetingPreparedPointsLoading = false,
  selectedMeetingPointId,
  selectedMeetingProjectId,
  meetingProjectReviews = [],
  meetingPointComposer,
  tensions,
  circles,
  employees = [],
  projects = [],
  projectSprints = {},
  onFetchProjectSprints,
  onFetchProjectMetrics,
  onFetchProjectObjectives,
  projectMetricsById,
  projectMetricsLoading,
  projectMetricsError,
  meetingPhase,
  onSelectMeetingPhase,
  user,
  meetingLoading,
  selectedCircleIsRole = false,
  onCloseMeeting,
  onImportTension,
  onTransferSecretary,
  onInviteParticipant,
  onMeetingDraftChange,
  onCreateMeetingTension,
  onRefreshMeetingTensions,
  onSelectMeetingTension,
  onLiveEditTension,
  onMeetingDecision,
  onAddMeetingPoint,
  onImportMeetingPoint,
  onSelectMeetingPoint,
  onPointDecision,
  onPointToTension,
  onPointNotesChange,
  onPointComposerUpdate,
  onCreatePointTask,
  onCreatePointObjective,
  onSelectMeetingProject,
  onFetchProjectReviews,
  onUpdateProjectReview,
  onNotesChange,
  onToggleForm,
  metricsRange,
  metricsRangeValid,
}) => {
  const effectiveUserId = user?.employee_id ?? user?.id;
  const userIsSecretary = String(activeMeeting?.created_by) === String(effectiveUserId);
  const meetingCircleId = activeMeeting?.circle_id || selectedCircle?.id;
  const secretary = meetingParticipants.find((p) => String(p.id) === String(activeMeeting?.created_by));
  const canEditMeetingTension = userIsSecretary;
  const meetingType = (activeMeeting?.type || 'gouvernance').toLowerCase();
  const isTactical = meetingType === 'tactique';
  const isPilotage = meetingType === 'pilotage';
  const showPoints = !isTactical;
  const [newPointTitle, setNewPointTitle] = useState('');
  const [inviteEmployeeId, setInviteEmployeeId] = useState('');
  const meetingParticipantIds = useMemo(
    () => new Set((meetingParticipants || []).map((participant) => String(participant.id))),
    [meetingParticipants]
  );
  const isParticipantConnected = useCallback(
    (participantId) => connectedUsers.some((u) => {
      const connectedId = u?.employee_id ?? u?.user_id ?? u?.id;
      return String(connectedId) === String(participantId);
    }),
    [connectedUsers]
  );
  const availableInvitees = useMemo(
    () => (employees || [])
      .filter((emp) => emp && emp.is_active !== false)
      .filter((emp) => !meetingParticipantIds.has(String(emp.id))),
    [employees, meetingParticipantIds]
  );
  const meetingScopeCircles = useMemo(() => {
    if (!meetingCircleId) return [];
    return (circles || []).filter((circle) =>
      String(circle.id) === String(meetingCircleId) || String(circle.parent_id) === String(meetingCircleId)
    );
  }, [circles, meetingCircleId]);
  const assignedMeetingRoles = useMemo(() => {
    if (!meetingScopeCircles.length || !effectiveUserId) return [];
    const userId = String(effectiveUserId);
    return meetingScopeCircles.filter((circle) => {
      if (!circle) return false;
      if (circle.owner_id !== undefined && String(circle.owner_id) === userId) return true;
      if (Array.isArray(circle.owner_ids)) {
        return circle.owner_ids.some((id) => String(id) === userId);
      }
      return false;
    });
  }, [meetingScopeCircles, effectiveUserId]);
  const expressedRoleOptions = userIsSecretary ? meetingScopeCircles : assignedMeetingRoles;

  // Filter tensions logic
  const filteredImportableTensions = useMemo(() => tensions.filter((t) => {
    if (!meetingCircleId) return false;
    const sameCircle = String(t.circle_id) === String(meetingCircleId);
    if (!sameCircle) return false;
    const status = (t.status || 'draft').toLowerCase();
    if (status !== 'draft') return false;
    if (userIsSecretary) return true;
    return String(t.author_id) === String(effectiveUserId);
  }), [tensions, meetingCircleId, userIsSecretary, effectiveUserId]);

  // Tension categorization
  const isPastMeetingTension = (status) => {
    const normalized = (status || '').toLowerCase();
    return normalized === 'applied' || normalized.startsWith('reject');
  };
  const pastMeetingTensions = meetingTensions.filter((t) => isPastMeetingTension(t.status));
  const activeMeetingTensions = meetingTensions.filter((t) => !isPastMeetingTension(t.status));

  const selectedTension = activeMeetingTensions.find((t) => String(t.id) === String(selectedMeetingTensionId)) || null;
  const getCircleSnapshot = React.useCallback(
    (circleId) => {
      if (!circleId) return null;
      const circle = (circles || []).find((c) => String(c.id) === String(circleId));
      if (!circle) return null;
      return {
        name: circle.name || '',
        purpose: circle.purpose || '',
        domains: circle.domains || [],
        accountabilities: circle.accountabilities || [],
        parent_id: circle.parent_id ?? null,
      };
    },
    [circles]
  );
  const parsedOperations = useMemo(() => {
    if (!selectedTension) return [];
    const ops = selectedTension.operations;
    if (Array.isArray(ops)) return ops;
    if (typeof ops === 'string') {
      try {
        const parsed = JSON.parse(ops);
        return Array.isArray(parsed) ? parsed : [];
      } catch (err) {
        return [];
      }
    }
    return [];
  }, [selectedTension]);
  const hasOperations = parsedOperations.length > 0;
  const primaryOp = parsedOperations.length > 0 ? parsedOperations[0] : null;
  const opAction = (primaryOp?.action || selectedTension?.action || '').toLowerCase();
  const opTargetId = primaryOp?.target_circle_id || primaryOp?.circle_id || selectedTension?.circle_id || null;

  // Derive snapshots so meeting view matches tension tab (prefilled + diff)
  const derivedOriginal = useMemo(() => {
    if (!selectedTension) return null;
    if (opAction === 'create') return null;
    if (primaryOp?.original_data) return primaryOp.original_data;
    const snapshot = getCircleSnapshot(opTargetId);
    if (snapshot) return snapshot;
    if (!hasOperations) {
      return selectedTension.original_data || null;
    }
    return null;
  }, [selectedTension, primaryOp, opAction, opTargetId, getCircleSnapshot, hasOperations]);

  const derivedProposed = useMemo(() => {
    if (!selectedTension) return null;
    if (primaryOp?.data && Object.keys(primaryOp.data || {}).length > 0) return primaryOp.data;
    if (!hasOperations) return selectedTension.proposed_data || null;
    return null;
  }, [selectedTension, primaryOp, hasOperations]);

  // --- No Local Draft State ---
  // Direct interaction with parent state via onLiveEditTension + Props

  const handleEdit = (field, value) => {
    if (!selectedTension) return;
    if (!onLiveEditTension) return;

    // Construct full update object based on current props + new value
    const baseProposed = derivedProposed || selectedTension.proposed_data || {};
    const nextProposed = { ...baseProposed };

    // Arrays need explicit handling to ensure they are arrays
    const currentDomains = nextProposed.domains || derivedOriginal?.domains || [];
    const currentAcc = nextProposed.accountabilities || derivedOriginal?.accountabilities || [];

    // Fields
    let nextTitle = selectedTension.title;

    if (field === 'title') {
      nextTitle = value;
    } else if (field === 'name') {
      nextProposed.name = value;
    } else if (field === 'purpose') {
      nextProposed.purpose = value;
    } else if (field === 'domains') {
      nextProposed.domains = value;
    } else if (field === 'accountabilities') {
      nextProposed.accountabilities = value;
    }

    const nextOperations = parsedOperations;
    const updatedOperations = primaryOp
      ? nextOperations.map((op, idx) => (idx === 0 ? { ...op, data: nextProposed } : op))
      : nextOperations;

    onLiveEditTension(selectedTension.id, {
      title: nextTitle,
      summary: selectedTension.summary,
      action: opAction || selectedTension.action,
      proposed_data: nextProposed,
      operations: updatedOperations,
      expressed_by_role_id: selectedTension.expressed_by_role_id,
    });
  };

  const persistCurrentDraftAndDecide = (decision) => {
    if (!selectedTension) return;
    onMeetingDecision && onMeetingDecision(selectedTension.id, decision);
  };

  // Helpers for display
  const currentProposed = selectedTension ? (derivedProposed || selectedTension.proposed_data || {}) : {};
  const isCreateAction = opAction === 'create';
  const currentOriginal = selectedTension
    ? (isCreateAction ? null : (derivedOriginal || selectedTension.original_data || {}))
    : {};

  // Computed values for inputs
  const displayTitle = selectedTension ? selectedTension.title : '';
  const displayName = selectedTension ? (currentProposed.name !== undefined ? currentProposed.name : currentOriginal?.name || '') : '';
  const displayPurpose = selectedTension ? (currentProposed.purpose !== undefined ? currentProposed.purpose : currentOriginal?.purpose || '') : '';

  // For lists, we need valid arrays
  const displayDomains = selectedTension ? (currentProposed.domains || currentOriginal?.domains || []) : [];
  const displayAccountabilities = selectedTension ? (currentProposed.accountabilities || currentOriginal?.accountabilities || []) : [];
  const originalDomains = selectedTension && Array.isArray(currentOriginal?.domains) ? currentOriginal.domains : null;
  const originalAccountabilities = selectedTension && Array.isArray(currentOriginal?.accountabilities) ? currentOriginal.accountabilities : null;
  const createParentInfo = useMemo(() => {
    if (!selectedTension || !isCreateAction) return null;
    const parentId =
      primaryOp?.data?.parent_id ??
      currentProposed?.parent_id ??
      primaryOp?.circle_id ??
      selectedTension.circle_id ??
      null;
    if (!parentId) return null;
    const parentCircle = (circles || []).find((c) => String(c.id) === String(parentId));
    return {
      id: parentId,
      name: parentCircle?.name || `Role #${parentId}`,
    };
  }, [selectedTension, isCreateAction, primaryOp, currentProposed, circles]);

  const updateMeetingDraftField = useCallback((key, value) => {
    if (!onMeetingDraftChange) return;
    onMeetingDraftChange((prev) => ({ ...(prev || {}), [key]: value }));
  }, [onMeetingDraftChange]);

  const selectedPoint = meetingPoints.find((point) => String(point.id) === String(selectedMeetingPointId)) || null;
  const visibleMeetingPoints = useMemo(
    () => meetingPoints.filter((point) => !['treated', 'closed'].includes((point.status || '').toLowerCase())),
    [meetingPoints]
  );
  const tensionStatusLabel = (status) => {
    const normalized = (status || 'draft').toLowerCase();
    if (normalized === 'draft') return 'Brouillon';
    if (normalized === 'meeting') return 'En réunion';
    if (normalized === 'proposal') return 'Proposition';
    if (normalized === 'applied') return 'Appliquée';
    if (normalized.startsWith('reject')) return 'Rejetée';
    return status || 'Brouillon';
  };
  const handleAddPoint = () => {
    const trimmed = (newPointTitle || '').trim();
    if (!trimmed) return;
    onAddMeetingPoint && onAddMeetingPoint(trimmed);
    setNewPointTitle('');
  };
  const statusLabel = (status) => {
    const norm = (status || 'open').toLowerCase();
    if (norm === 'closed') return 'Clôture';
    if (norm === 'treated') return 'Traité';
    if (norm === 'converted') return 'Converti';
    return 'Ouvert';
  };


  // If no meeting open
  if (!activeMeeting) {
    return <MeetingPanelEmptyState selectedCircleIsRole={selectedCircleIsRole} />;
  }


  if (isTactical) {
    return (
      <MeetingPanelTactical
        activeMeeting={activeMeeting}
        selectedCircle={selectedCircle}
        meetingError={meetingError}
        meetingParticipants={meetingParticipants}
        meetingPoints={meetingPoints}
        preparedPoints={meetingPreparedPoints}
        preparedPointsLoading={meetingPreparedPointsLoading}
        selectedMeetingPointId={selectedMeetingPointId}
        selectedMeetingProjectId={selectedMeetingProjectId}
        meetingProjectReviews={meetingProjectReviews}
        meetingPointComposer={meetingPointComposer}
        connectedUsers={connectedUsers}
        realtimeConnected={realtimeConnected}
        presenceCount={presenceCount}
        employees={employees}
        projects={projects}
        projectSprints={projectSprints}
        onFetchProjectSprints={onFetchProjectSprints}
        onFetchProjectMetrics={onFetchProjectMetrics}
        onFetchProjectObjectives={onFetchProjectObjectives}
        projectMetricsById={projectMetricsById}
        projectMetricsLoading={projectMetricsLoading}
        projectMetricsError={projectMetricsError}
        meetingPhase={meetingPhase}
        onSelectMeetingPhase={onSelectMeetingPhase}
        circles={circles}
        userIsSecretary={userIsSecretary}
        onCloseMeeting={onCloseMeeting}
        onTransferSecretary={onTransferSecretary}
        onInviteParticipant={onInviteParticipant}
        onAddMeetingPoint={onAddMeetingPoint}
        onImportMeetingPoint={onImportMeetingPoint}
        onSelectMeetingPoint={onSelectMeetingPoint}
        onPointDecision={onPointDecision}
        onPointNotesChange={onPointNotesChange}
        onPointComposerUpdate={onPointComposerUpdate}
        onCreatePointTask={onCreatePointTask}
        onCreatePointObjective={onCreatePointObjective}
        onSelectMeetingProject={onSelectMeetingProject}
        onFetchProjectReviews={onFetchProjectReviews}
        onUpdateProjectReview={onUpdateProjectReview}
        metricsRange={metricsRange}
        metricsRangeValid={metricsRangeValid}
      />
    );
  }

  if (isPilotage) {
    return (
      <MeetingPanelPilotage
        activeMeeting={activeMeeting}
        selectedCircle={selectedCircle}
        meetingError={meetingError}
        meetingParticipants={meetingParticipants}
        selectedMeetingProjectId={selectedMeetingProjectId}
        meetingProjectReviews={meetingProjectReviews}
        connectedUsers={connectedUsers}
        realtimeConnected={realtimeConnected}
        presenceCount={presenceCount}
        employees={employees}
        projects={projects}
        circles={circles}
        projectSprints={projectSprints}
        onFetchProjectSprints={onFetchProjectSprints}
        onFetchProjectReviews={onFetchProjectReviews}
        onUpdateProjectReview={onUpdateProjectReview}
        userIsSecretary={userIsSecretary}
        onCloseMeeting={onCloseMeeting}
        onTransferSecretary={onTransferSecretary}
        onInviteParticipant={onInviteParticipant}
        onSelectMeetingProject={onSelectMeetingProject}
      />
    );
  }


  return (
    <div className="bg-white p-6 rounded-lg shadow-md h-auto overflow-visible flex flex-col">
      {/* Header Section */}
      <div className="flex items-start justify-between mb-6 border-b border-slate-100 pb-4">
        <div>
          <div className="flex items-center gap-2 text-xs font-bold text-indigo-600 uppercase tracking-wider mb-1">
            <RefreshCcw size={12} /> Réunion de gouvernance
          </div>
          <h2 className="text-2xl font-bold text-slate-900">
            {selectedCircle?.name || 'Réunion'}
          </h2>
          <p className="text-xs text-slate-400 mt-1">
            Secrétaire : {secretary?.name || 'Non défini'}
          </p>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-3">
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border ${realtimeConnected ? 'bg-emerald-50 border-emerald-100 text-emerald-700' : 'bg-slate-50 border-slate-200 text-slate-500'}`}>
            <div className={`w-2 h-2 rounded-full ${realtimeConnected ? 'bg-emerald-500 animate-pulse' : 'bg-slate-400'}`} />
            {realtimeConnected ? 'Synchro en direct' : 'Hors ligne'}
            {presenceCount > 0 && <span className="ml-1 opacity-75">({presenceCount})</span>}
          </div>

          {userIsSecretary && (
            <div className="flex gap-2">
              <button
                onClick={() => onToggleForm && onToggleForm()}
                className="px-3 py-1.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
              >
                {meetingFormVisible ? 'Masquer Formulaire' : '+ Nouvelle Tension'}
              </button>
              <button
                onClick={onCloseMeeting}
                className="px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 border border-red-100 rounded-lg hover:bg-red-100 transition-colors"
              >
                Clôturer
              </button>
            </div>
          )}
        </div>
      </div>

      {meetingError && (
        <div className="mb-4 flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-100 px-4 py-3 rounded-xl">
          <AlertCircle size={16} />
          {meetingError}
        </div>
      )}

      <div className="grid grid-cols-12 gap-6 h-auto overflow-visible">
        {/* Left Sidebar: Context & Tensions List */}
        <div className="col-span-12 lg:col-span-4 xl:col-span-3 flex flex-col gap-4 h-auto overflow-visible pr-2">
          {/* Participants Card */}
          <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Participants</h3>
            <div className="flex flex-wrap gap-2">
              {meetingParticipants.map((p, idx) => {
                const isSecretary = String(p.id) === String(activeMeeting?.created_by);
                const isConnected = isParticipantConnected(p.id);
                const canTransfer = userIsSecretary && !isSecretary && isConnected && Boolean(onTransferSecretary);
                return (
                  <div
                    key={idx}
                    onClick={() => {
                      if (canTransfer) onTransferSecretary(p.id);
                    }}
                    title={canTransfer ? "Transférer le rôle de secrétaire" : undefined}
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${isConnected ? 'bg-white border-emerald-200 text-emerald-700 shadow-sm' : 'bg-slate-100 border-slate-200 text-slate-500'} ${canTransfer ? 'cursor-pointer hover:bg-indigo-50 hover:border-indigo-200' : ''}`}
                  >
                    <div className={`w-1.5 h-1.5 rounded-full ${isConnected ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                    {p.name}
                    {isSecretary && (
                      <span className="ml-1 text-[9px] uppercase px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 font-semibold">
                        Secrétaire
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
            {userIsSecretary && onInviteParticipant && (
              <div className="mt-3 flex flex-wrap gap-2">
                <select
                  value={inviteEmployeeId}
                  onChange={(e) => setInviteEmployeeId(e.target.value)}
                  className="flex-1 min-w-[180px] border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:ring-2 focus:ring-indigo-100 outline-none"
                  disabled={availableInvitees.length === 0}
                >
                  <option value="">
                    {availableInvitees.length === 0 ? 'Aucune personne a inviter' : 'Inviter une personne'}
                  </option>
                  {availableInvitees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.name}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => {
                    if (!inviteEmployeeId) return;
                    onInviteParticipant(Number(inviteEmployeeId));
                    setInviteEmployeeId('');
                  }}
                  className="px-3 py-1.5 text-xs font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                  disabled={!inviteEmployeeId}
                >
                  Inviter
                </button>
              </div>
            )}
          </div>

          {/* Import + Notes + Historique */}
          <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Importer une tension</h3>
                {!userIsSecretary && <span className="text-[10px] text-slate-400">Vos tensions</span>}
              </div>
              <select
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-100 outline-none"
                value=""
                onChange={(e) => {
                  const val = e.target.value;
                  if (val) {
                    onImportTension && onImportTension(Number(val));
                  }
                }}
              >
                <option value="">Choisir une tension</option>
                {filteredImportableTensions.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.title || t.original_data?.name || 'Tension'}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Bloc-notes</h3>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${realtimeConnected ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                  {realtimeConnected ? 'En direct' : 'Hors ligne'}
                </span>
              </div>
              <textarea
                value={meetingNotes || ''}
                onChange={(e) => onNotesChange && onNotesChange(e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm min-h-[120px] focus:ring-2 focus:ring-indigo-100 outline-none"
                placeholder="Notes rapides..."
              />
            </div>

            {showPoints && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Points</h3>
                  <span className="text-[10px] text-slate-400">{visibleMeetingPoints.length}</span>
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newPointTitle}
                    onChange={(e) => setNewPointTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleAddPoint();
                    }}
                    className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-100 outline-none"
                    placeholder="Ajouter un point..."
                  />
                  <button
                    onClick={handleAddPoint}
                    className="px-3 py-2 text-xs font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                    disabled={!newPointTitle.trim()}
                  >
                    Ajouter
                  </button>
                </div>
                {visibleMeetingPoints.length === 0 ? (
                  <p className="text-xs text-slate-400 italic">Aucun point pour le moment.</p>
                ) : (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {visibleMeetingPoints.map((point) => {
                      const isSelected = String(selectedMeetingPointId) === String(point.id);
                      const isDone = ['treated', 'converted'].includes((point.status || '').toLowerCase());
                      return (
                        <div
                          key={point.id}
                          onClick={() => userIsSecretary && onSelectMeetingPoint && onSelectMeetingPoint(point.id)}
                          className={`p-2 rounded-lg border text-sm transition-colors ${isSelected ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-200'} ${userIsSecretary ? 'cursor-pointer' : ''}`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className={`font-medium ${isDone ? 'text-slate-400 line-through' : 'text-slate-800'}`}>
                              {point.title}
                            </div>
                            <span className="text-[10px] uppercase px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-semibold">
                              {statusLabel(point.status)}
                            </span>
                          </div>
                          <div className="text-[11px] text-slate-500 mt-0.5">
                            Par {point.author_name || 'un membre'}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {userIsSecretary && selectedPoint && (selectedPoint.status || 'open').toLowerCase() === 'open' && (
                  <div className="flex gap-2 pt-2">
                    <button
                      onClick={() => onPointDecision && onPointDecision(selectedPoint.id, 'treated')}
                      className="flex-1 py-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-100 rounded hover:bg-emerald-100"
                    >
                      Traiter
                    </button>
                    <button
                      onClick={() => onPointToTension && onPointToTension(selectedPoint)}
                      className="flex-1 py-1.5 text-xs font-semibold text-indigo-700 bg-indigo-50 border border-indigo-100 rounded hover:bg-indigo-100"
                    >
                      Transformer en tension
                    </button>
                  </div>
                )}
              </div>
            )}

            {pastMeetingTensions.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Historique (passées)</h3>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {pastMeetingTensions.map((tension) => (
                    <div key={tension.id} className="p-3 rounded-lg border border-slate-200 bg-slate-50">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold text-slate-900 truncate">
                          {tension.title || tension.original_data?.name || 'Tension'}
                        </p>
                        <span className="text-[10px] uppercase px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-semibold">
                          {tensionStatusLabel(tension.status)}
                        </span>
                      </div>
                      {tension.summary && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{tension.summary}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Tensions List */}
          <div className="flex-1 flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Tensions ({activeMeetingTensions.length})</h3>
              <button onClick={() => activeMeeting && onRefreshMeetingTensions(activeMeeting.id)} className="text-slate-400 hover:text-indigo-600 transition-colors">
                <RefreshCcw size={14} />
              </button>
            </div>

            <div className="space-y-2">
              {activeMeetingTensions.length === 0 ? (
                <p className="text-sm text-slate-400 italic text-center py-4">Aucune tension en cours</p>
              ) : (
                activeMeetingTensions.map(tension => (
                  <div
                    key={tension.id}
                    onClick={() => onSelectMeetingTension(tension.id)}
                    className={`group cursor-pointer p-3 rounded-xl border transition-all duration-200 ${String(selectedMeetingTensionId) === String(tension.id) ? 'bg-indigo-50 border-indigo-200 shadow-sm' : 'bg-white border-slate-200 hover:border-indigo-200 hover:shadow-sm'}`}
                  >
                    <div className="flex justify-between items-start mb-1">
                      <span className={`text-sm font-semibold ${String(selectedMeetingTensionId) === String(tension.id) ? 'text-indigo-900' : 'text-slate-700'}`}>
                        {tension.title || tension.original_data?.name || 'Sans titre'}
                      </span>
                      {String(tension.author_id) === String(effectiveUserId) && (
                        <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">Moi</span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 line-clamp-2">{tension.summary || 'Pas de description'}</p>

                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Main Content: Editor */}
        <div className="col-span-12 lg:col-span-8 xl:col-span-9 flex flex-col h-auto overflow-visible bg-slate-50/50 rounded-xl border border-slate-200">
          {/* New Tension Form Overlay */}
          {meetingFormVisible && (
            <div className="p-4 bg-white border-b border-slate-200 shadow-sm z-10">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-bold text-slate-800">Nouvelle Tension</h3>
                {userIsSecretary && (
                  <button onClick={() => onToggleForm(false)} className="text-slate-400 hover:text-slate-600">
                    <X size={18} />
                  </button>
                )}
              </div>
              <TensionEditor
                tensionDraft={meetingDraft}
                circles={meetingScopeCircles}
                user={user}
                tensionVotes={[]}
                tensionVotesLoading={false}
                tensionsLoading={meetingLoading}
                isFieldChanged={() => false}
                onUpdateField={updateMeetingDraftField}
                onUpdateProposedField={() => { }}
                onUpdateList={() => { }}
                onChangeAction={() => { }}
                onDelete={() => { }}
                onPropose={() => { }}
                onWithdraw={() => { }}
                onCancel={() => userIsSecretary && onToggleForm(false)}
                onSave={() => userIsSecretary && onCreateMeetingTension()}
                mode="meeting"
                readOnly={!userIsSecretary}
                expressedRoleOptions={expressedRoleOptions}
                defaultCircleId={meetingCircleId}
              />
            </div>
          )}

          {/* Editor Area */}
          <div className="flex-1 h-auto overflow-visible p-6">
            {selectedTension ? (
              <div className="max-w-4xl mx-auto space-y-6">

                {/* Tension Title & Context */}
                <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex flex-col gap-4">
                  <div className="flex items-start gap-4">
                    <div className="mt-1 w-10 h-10 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center font-bold text-lg">
                      T
                    </div>
                    <div className="flex-1">
                      <SmartDiffInput
                        label="Titre de la tension"
                        originalValue={null}
                        currentValue={displayTitle}
                        onChange={(v) => handleEdit('title', v)}
                        inputClassName="w-full text-lg font-bold text-slate-800 border-none px-0 py-0 focus:ring-0 placeholder-slate-300 bg-transparent"
                        hideLabel
                        placeholder="Saisissez le titre de la tension..."
                        disabled={!canEditMeetingTension}
                      />
                      <p className="text-xs text-slate-400 mt-1">Proposé par {selectedTension.author_name || 'un membre'}</p>
                    </div>
                  </div>
                </div>

                {/* Role Card Visualization (Post-Proposal State) */}
                <div className="border border-slate-200 rounded-xl bg-white shadow-lg overflow-hidden relative">

                  {/* Card Header: Role Identity */}
                  <div className="bg-slate-50/80 p-6 border-b border-slate-100 relative group">
                    <div className="absolute top-4 right-4 flex gap-2">
                      {isCreateAction && (
                        <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-[10px] font-bold uppercase rounded-md tracking-wide">Nouveau Rôle</span>
                      )}
                      {opAction === 'delete' && (
                        <span className="px-2 py-1 bg-red-100 text-red-700 text-[10px] font-bold uppercase rounded-md tracking-wide">Suppression</span>
                      )}
                    </div>

                    <div className="space-y-4">
                      <SmartDiffInput
                        label="Nom du Rôle"
                        originalValue={isCreateAction ? null : currentOriginal?.name}
                        currentValue={displayName}
                        onChange={(v) => handleEdit('name', v)}
                        hideLabel
                        inputClassName="w-full text-2xl font-black text-slate-800 bg-transparent border-0 border-b-2 border-transparent hover:border-slate-200 focus:border-indigo-400 focus:ring-0 px-0 py-1 transition-all placeholder-slate-300"
                        placeholder="Nom du Rôle"
                        disabled={!canEditMeetingTension}
                      />

                      <SmartDiffInput
                        label="Raison d'être"
                        originalValue={isCreateAction ? null : currentOriginal?.purpose}
                        currentValue={displayPurpose}
                        onChange={(v) => handleEdit('purpose', v)}
                        multiline
                        hideLabel
                        inputClassName="w-full text-sm italic text-slate-600 bg-transparent border-0 focus:ring-0 px-0 resize-none placeholder-slate-300 min-h-[3rem]"
                        placeholder="Définir une raison d'être..."
                        disabled={!canEditMeetingTension}
                      />
                      {isCreateAction && createParentInfo && (
                        <div className="text-xs text-slate-500">
                          <span className="font-semibold text-slate-600">Parent :</span> {createParentInfo.name}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Card Body: Domains & Accountabilities */}
                  <div className="grid grid-cols-1 md:grid-cols-2">
                    <div className="p-6 border-b md:border-b-0 md:border-r border-slate-100 bg-white">
                      <div className="mb-3 flex items-center justify-between">
                        <h4 className="font-bold text-slate-800 text-sm uppercase tracking-wider">Domaines</h4>
                      </div>
                      <SmartDiffList
                        label="Domaines"
                        originalList={isCreateAction ? null : originalDomains}
                        currentList={displayDomains}
                        onChange={(v) => handleEdit('domains', v)}
                        disabled={!canEditMeetingTension}
                      />
                    </div>

                    <div className="p-6 bg-white">
                      <div className="mb-3 flex items-center justify-between">
                        <h4 className="font-bold text-slate-800 text-sm uppercase tracking-wider">Redevabilités</h4>
                      </div>
                      <SmartDiffList
                        label="Redevabilités"
                        originalList={isCreateAction ? null : originalAccountabilities}
                        currentList={displayAccountabilities}
                        onChange={(v) => handleEdit('accountabilities', v)}
                        disabled={!canEditMeetingTension}
                      />
                    </div>
                  </div>
                </div>

                {userIsSecretary && (
                  <div className="flex gap-3 justify-end pt-2">
                    <button
                      onClick={() => persistCurrentDraftAndDecide('reject')}
                      className="px-4 py-2 text-sm font-semibold text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50"
                    >
                      Rejeter
                    </button>
                    <button
                      onClick={() => persistCurrentDraftAndDecide('accept')}
                      className="px-4 py-2 text-sm font-semibold text-emerald-600 bg-white border border-emerald-200 rounded-lg hover:bg-emerald-50"
                    >
                      Accepter
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-slate-400">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                  <ArrowRight size={24} className="text-slate-300" />
                </div>
                <p>Sélectionnez une tension pour voir les détails</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeetingPanel;
